import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Box,
  Stack,
  Typography,
  IconButton,
  useMediaQuery,
  Skeleton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StyledButton from '@/components/common/styledButton';
import { useLocalization } from '@/hooks/useLocalization';
import InvitationTypeSelector from './components/InvitationTypeSelector';
import SavedInvitationSelector from './components/SavedInvitationSelector';
import InvitationCustomEditor from './components/InvitationCustomEditor';
import { buildInvitationPreviewHtml } from './utils/invitationTemplatePreview';
import {
  useGetInvestorInviteSettingQuery,
  useGetInvestorInviteSettingForceAdvisorGroupQuery,
  useGetInvestorInviteSettingForceAdvisorQuery,
  useGetInvestorMultipleInviteSettingQuery,
  useGetMultipleInvitesForInvestorQuery,
  useUpdateInvestorInviteSettingMutation,
  useUpdateSelectedMultipleInviteMutation,
  type InvestorInviteSetting,
  type MultipleInvitesForInvestorResponse,
  type InvestorMultipleInviteSettingResponse,
  type UpdateInviteSettingRequest,
} from '@/store/clientsApi';

export type InvitationType = 'default' | 'custom' | 'saved';

type EditInvitationTemplateModalProps = {
  open: boolean;
  onClose: () => void;
  isFullscreen?: boolean;
  investorId?: number;
  inviteSettings?: InvestorInviteSetting;
  // Optional props accepted from parent (not required for modal logic)
  investorName?: string;
  useLastName?: boolean;
  additionalMessage?: string;
  signature?: string;
  invitorName?: string;
};

const EditInvitationTemplateModal = ({
  open,
  onClose,
  isFullscreen = false,
  investorId,
  inviteSettings,
}: EditInvitationTemplateModalProps) => {
  const { t } = useLocalization();
  // Preview is static for parity; user info not required here

  const [invitationType, setInvitationType] =
    useState<InvitationType>('default');
  const [previewHtml, setPreviewHtml] = useState<string>('');
  type OverrideSettings = Partial<UpdateInviteSettingRequest> &
    Partial<InvestorInviteSetting>;
  const [overrideSettings, setOverrideSettings] =
    useState<OverrideSettings | null>(null);
  const [editHtml, setEditHtml] = useState<string>('');
  const [userChangedSelection, setUserChangedSelection] =
    useState<boolean>(false);
  const [useLastName, setUseLastName] = useState<boolean>(false);
  const [customSignature, setCustomSignature] = useState<string>('');
  const [useGroupName, setUseGroupName] = useState<boolean>(true);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  // Legacy-parity data loads (trigger only when open)
  const { data: forceGroupSettings, refetch: refetchGroupSettings } =
    useGetInvestorInviteSettingForceAdvisorGroupQuery(undefined, {
      skip: !open,
    });
  const { data: forceAdvisorSettings, refetch: refetchAdvisorSettings } =
    useGetInvestorInviteSettingForceAdvisorQuery(undefined, { skip: !open });

  const { data: savedInviteList } = useGetInvestorMultipleInviteSettingQuery(
    investorId!,
    { skip: !open || !investorId },
  );
  const { data: multipleInvites, refetch: refetchMultipleInvites } =
    useGetMultipleInvitesForInvestorQuery(investorId!, {
      skip: !open || !investorId,
    });
  // Always fetch base settings when this modal mounts/opens (legacy parity)
  const {
    data: baseInviteSettings,
    refetch: refetchBaseInviteSettings,
    isFetching: isFetchingBase,
  } = useGetInvestorInviteSettingQuery(undefined, {
    skip: !open,
    refetchOnMountOrArgChange: true,
  });
  useEffect(() => {
    if (open) {
      refetchBaseInviteSettings();
    }
  }, [open, refetchBaseInviteSettings]);

  const effectiveInviteSettings = (baseInviteSettings ?? inviteSettings) as
    | InvestorInviteSetting
    | undefined;

  // Legacy-derived flags for selection when reopening the modal
  const hasRecords = (
    resp: MultipleInvitesForInvestorResponse,
  ): resp is {
    Records: {
      MultipleInviteId: number | string;
      InviteName?: string | null;
      Content?: string | null;
    }[];
  } => {
    return (
      typeof (resp as { Records?: unknown }).Records !== 'undefined' &&
      Array.isArray((resp as { Records?: unknown }).Records)
    );
  };

  const getFirstSavedEntry = (
    resp?: MultipleInvitesForInvestorResponse,
  ):
    | {
        MultipleInviteId?: number | string;
        InviteName?: string | null;
        Content?: string | null;
      }
    | undefined => {
    if (!resp) return undefined;
    if (hasRecords(resp)) return resp.Records[0];
    return resp;
  };

  const hasSaved = useMemo(() => {
    const first = getFirstSavedEntry(multipleInvites);
    return (
      typeof first?.Content === 'string' && first.Content.trim().length > 0
    );
  }, [multipleInvites]);

  const advisorActive = useMemo(() => {
    const fa = forceAdvisorSettings as
      | Partial<{ IsActive: boolean; AdvisorId: number | string }>
      | undefined;
    return !!fa?.IsActive && Number(fa?.AdvisorId) > 0;
  }, [forceAdvisorSettings]);

  const savedOptions = useMemo(() => {
    const src = savedInviteList as
      | InvestorMultipleInviteSettingResponse
      | undefined;
    if (!src)
      return [] as Array<{
        MultipleInviteId: number | string;
        InviteName?: string | null;
      }>;
    if (Array.isArray(src)) return src;
    if ('Records' in src && Array.isArray(src.Records)) return src.Records;
    return [] as Array<{
      MultipleInviteId: number | string;
      InviteName?: string | null;
    }>;
  }, [savedInviteList]);

  const hasSavedOptions = savedOptions.length > 0;

  // Initialize radio selection once per open to mirror legacy behavior
  const [initializedForOpen, setInitializedForOpen] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [selectedSavedId, setSelectedSavedId] = useState<
    string | number | undefined
  >(undefined);

  // Clear any previous overrides when opening to avoid stale state
  useEffect(() => {
    if (open) {
      setOverrideSettings(null);
      setEditHtml('');
    }
  }, [open]);

  // Force-refetch on open and derive initial selection from fresh results
  useEffect(() => {
    if (!open) return;
    if (userChangedSelection) return;
    if (initializedForOpen || initializing) return;

    const doInit = async () => {
      try {
        setInitializing(true);
        type RefetchResult<T = unknown> = { data?: T };
        const promises: Array<Promise<RefetchResult>> = [];
        promises.push(refetchAdvisorSettings());
        promises.push(refetchGroupSettings());
        if (typeof investorId === 'number')
          promises.push(refetchMultipleInvites());
        promises.push(refetchBaseInviteSettings());
        const results = await Promise.all(promises);

        const advisorData = (results[0]?.data ?? forceAdvisorSettings) as
          | {
              IsActive?: boolean;
              AdvisorId?: number | string;
              Content?: string;
            }
          | undefined;
        /* const groupData = (results[1]?.data ?? forceGroupSettings) as any; */
        const savedData = (
          typeof investorId === 'number'
            ? (results[2]?.data ?? multipleInvites)
            : undefined
        ) as MultipleInvitesForInvestorResponse | undefined;
        // base settings may be at results[2] or results[3] depending on investorId existence
        const baseIdx = typeof investorId === 'number' ? 3 : 2;
        const baseData = (results[baseIdx]?.data ?? effectiveInviteSettings) as
          | InvestorInviteSetting
          | undefined;

        const hasSavedFromServer = (() => {
          const first = getFirstSavedEntry(savedData);
          if (!first) return false;
          if (
            typeof first.Content === 'string' &&
            first.Content.trim().length > 0
          )
            return true;
          // Fallback: presence of a record counts as saved (legacy behavior)
          return true;
        })();

        const advisorIsActive =
          !!advisorData?.IsActive && Number(advisorData?.AdvisorId) > 0;
        const nextType: InvitationType = hasSavedFromServer
          ? 'saved'
          : advisorIsActive
            ? 'custom'
            : 'default';
        setInvitationType(nextType);

        if (nextType === 'saved') {
          const saved = getFirstSavedEntry(savedData);
          if (saved && typeof saved.Content === 'string')
            setOverrideSettings({
              ...(baseData || {}),
              Content: saved.Content,
            });
        } else if (nextType === 'custom') {
          const initial = String(
            advisorData?.Content ||
              baseData?.Content ||
              '<p>*|salutation|*</p>\n\n\n\n<p>*|groupname|*</p>',
          );
          setEditHtml(initial);
        }

        setInitializedForOpen(true);
      } finally {
        setInitializing(false);
      }
    };

    void doInit();
  }, [
    open,
    userChangedSelection,
    initializedForOpen,
    initializing,
    investorId,
    refetchAdvisorSettings,
    refetchGroupSettings,
    refetchMultipleInvites,
    refetchBaseInviteSettings,
    forceAdvisorSettings,
    forceGroupSettings,
    multipleInvites,
    effectiveInviteSettings,
  ]);
  useEffect(() => {
    if (!open) {
      setInitializedForOpen(false);
      setUserChangedSelection(false);
      return;
    }
    if (initializedForOpen || userChangedSelection || initializing) return;

    // Wait until key data is available: advisor/group; and saved (if investorId provided)
    const advisorLoaded =
      typeof forceAdvisorSettings !== 'undefined' &&
      typeof forceGroupSettings !== 'undefined';
    const savedLoaded =
      typeof investorId === 'number'
        ? typeof multipleInvites !== 'undefined'
        : true;
    if (!advisorLoaded || !savedLoaded) return;

    const nextType: InvitationType = hasSaved
      ? 'saved'
      : advisorActive
        ? 'custom'
        : 'default';
    setInvitationType(nextType);

    if (nextType === 'saved') {
      const saved = getFirstSavedEntry(multipleInvites);
      if (saved && typeof saved.Content === 'string')
        setOverrideSettings({
          ...(effectiveInviteSettings || {}),
          Content: saved.Content,
        });
    } else if (nextType === 'custom') {
      const initial = String(
        (forceAdvisorSettings as unknown as { Content?: string } | undefined)
          ?.Content ||
          effectiveInviteSettings?.Content ||
          '<p>*|salutation|*</p>\n\n\n\n<p>*|groupname|*</p>',
      );
      setEditHtml(initial);
    }

    setInitializedForOpen(true);
  }, [
    open,
    initializedForOpen,
    userChangedSelection,
    initializing,
    hasSaved,
    advisorActive,
    multipleInvites,
    forceAdvisorSettings,
    forceGroupSettings,
    effectiveInviteSettings,
    investorId,
  ]);

  // If saved becomes available after initial advisor/group load, prefer it (unless user already changed)
  useEffect(() => {
    if (!open) return;
    if (userChangedSelection) return;
    if (!initializedForOpen) return;
    if (!hasSaved) return;
    if (invitationType === 'saved') return;

    setInvitationType('saved');
    const saved = getFirstSavedEntry(multipleInvites);
    if (saved && typeof saved.Content === 'string') {
      const merged: OverrideSettings = {
        ...(effectiveInviteSettings || {}),
        Content: saved.Content,
      };
      setOverrideSettings(merged);
    }
  }, [
    open,
    hasSaved,
    initializedForOpen,
    userChangedSelection,
    invitationType,
    multipleInvites,
    effectiveInviteSettings,
  ]);

  // If there are no saved templates available, avoid being stuck in saved mode
  useEffect(() => {
    if (!open) return;
    if (invitationType !== 'saved') return;
    if (hasSavedOptions) return;
    setInvitationType(advisorActive ? 'custom' : 'default');
  }, [open, invitationType, hasSavedOptions, advisorActive]);

  // When entering saved mode, seed selectedSavedId from server if available
  useEffect(() => {
    if (!open) return;
    if (invitationType !== 'saved') return;
    const saved = getFirstSavedEntry(multipleInvites);
    if (
      saved &&
      saved.MultipleInviteId !== undefined &&
      saved.MultipleInviteId !== null
    ) {
      setSelectedSavedId(saved.MultipleInviteId);
    }
  }, [open, invitationType, multipleInvites]);

  const activeSettings:
    | (Partial<UpdateInviteSettingRequest> & Partial<InvestorInviteSetting>)
    | undefined = useMemo(() => {
    if (overrideSettings) return overrideSettings;
    if (!open) return effectiveInviteSettings;
    if (invitationType === 'saved' && multipleInvites) {
      const saved = getFirstSavedEntry(multipleInvites);
      if (saved && typeof saved.Content === 'string') {
        return { ...(effectiveInviteSettings || {}), Content: saved.Content };
      }
    }
    // Determine advisor vs group settings similar to legacy
    const base = advisorActive ? forceAdvisorSettings : forceGroupSettings;
    return base ?? effectiveInviteSettings;
  }, [
    open,
    invitationType,
    multipleInvites,
    forceAdvisorSettings,
    forceGroupSettings,
    effectiveInviteSettings,
    overrideSettings,
    advisorActive,
  ]);

  const DEFAULT_CONTENT = '<p>*|salutation|*</p>\n\n\n\n<p>*|groupname|*</p>';

  const processedHtml: string = useMemo(() => {
    const source = (activeSettings ||
      effectiveInviteSettings ||
      {}) as Partial<UpdateInviteSettingRequest> &
      Partial<InvestorInviteSetting>;
    // When customizing, prefer live edits for preview; otherwise use stored content
    const rawContent =
      invitationType === 'custom' && isEditing && editHtml
        ? editHtml
        : String(source.Content || '');
    const signatureReplacement =
      invitationType === 'custom'
        ? String(customSignature || source.CustomSignature || '')
        : String(source.CustomSignature || '');
    const name = (
      invitationType === 'custom' ? useGroupName : !!source.UseGroupName
    )
      ? String(source.AdvisorGroupName || '')
      : String(source.AdvisorName || '');

    return buildInvitationPreviewHtml({
      content: rawContent,
      signature: signatureReplacement,
      senderName: name,
    });
  }, [
    activeSettings,
    effectiveInviteSettings,
    invitationType,
    editHtml,
    customSignature,
    useGroupName,
    isEditing,
  ]);

  const [updateInviteSetting] = useUpdateInvestorInviteSettingMutation();
  const [updateSelectedMultipleInvite] =
    useUpdateSelectedMultipleInviteMutation();

  // Handlers to mirror legacy PUT calls when switching options
  const onUseDefault = async () => {
    if (!effectiveInviteSettings) return;
    const source =
      effectiveInviteSettings as Partial<UpdateInviteSettingRequest> &
        InvestorInviteSetting;
    const body: UpdateInviteSettingRequest = {
      IsActive: false,
      AdvisorGroupEmailIsActive: true,
      MultipleInviteId: null,
      InviteName: null,
      Content: DEFAULT_CONTENT,
      UseGroupName: true,
      UseLastName: false,
      CustomSignature: effectiveInviteSettings.CustomSignature || 'Best,',
      AdvisorId: source.AdvisorId ?? null,
      AdvisorGroupId: source.AdvisorGroupId ?? null,
      AdvisorName: effectiveInviteSettings.AdvisorName,
      AdvisorGroupName: effectiveInviteSettings.AdvisorGroupName,
      LoggedInUserName: effectiveInviteSettings.LoggedInUserName ?? null,
      IncludeScheduleAssistance: false,
    };
    setIsSwitching(true);
    try {
      await updateInviteSetting(body).unwrap();
      const refreshed = await refetchGroupSettings();
      if (refreshed?.data)
        setOverrideSettings(refreshed.data as InvestorInviteSetting);
    } catch {
      console.error('Failed to update invite setting');
    }
    setInvitationType('default');
    setUserChangedSelection(true);
    setIsEditing(false);
    setIsSwitching(false);
  };

  const onUseCustomized = async () => {
    if (!effectiveInviteSettings) return;
    const source =
      effectiveInviteSettings as Partial<UpdateInviteSettingRequest> &
        InvestorInviteSetting;
    const body: UpdateInviteSettingRequest = {
      IsActive: true,
      AdvisorGroupEmailIsActive: false,
      MultipleInviteId: null,
      InviteName: null,
      Content: DEFAULT_CONTENT,
      UseGroupName: true,
      UseLastName: false,
      CustomSignature: effectiveInviteSettings.CustomSignature || 'Best,',
      AdvisorId: source.AdvisorId ?? null,
      AdvisorGroupId: source.AdvisorGroupId ?? null,
      AdvisorName: effectiveInviteSettings.AdvisorName,
      AdvisorGroupName: effectiveInviteSettings.AdvisorGroupName,
      LoggedInUserName: effectiveInviteSettings.LoggedInUserName ?? null,
      IncludeScheduleAssistance: false,
    };
    setIsSwitching(true);
    try {
      await updateInviteSetting(body).unwrap();
      const refreshed = await refetchAdvisorSettings();
      if (refreshed?.data)
        setOverrideSettings(refreshed.data as InvestorInviteSetting);
    } catch {
      console.error('Failed to update invite setting');
    }
    // Initialize edit buffer with either saved content or default base
    setInvitationType('custom');
    const initial = String(
      overrideSettings?.Content ||
        effectiveInviteSettings.Content ||
        body.Content,
    );
    setEditHtml(initial);
    setUserChangedSelection(true);
    setIsEditing(false);
    setIsSwitching(false);
  };

  const onUseSaved = () => {
    setInvitationType('saved');
    const saved = getFirstSavedEntry(multipleInvites);
    if (saved && typeof saved.Content === 'string')
      setOverrideSettings({
        ...(effectiveInviteSettings || {}),
        Content: saved.Content,
      });
    setSelectedSavedId(saved?.MultipleInviteId ?? undefined);
    setUserChangedSelection(true);
    setIsEditing(false);
  };

  const onChangeSavedOption = async (value: string | number) => {
    setIsSwitching(true);
    try {
      await updateSelectedMultipleInvite({ multipleInviteId: value }).unwrap();
      // Refresh saved content and base settings similar to legacy
      const [savedRes] = await Promise.all([
        refetchMultipleInvites(),
        refetchAdvisorSettings(),
        refetchGroupSettings(),
      ]);
      const saved = getFirstSavedEntry(
        savedRes?.data as MultipleInvitesForInvestorResponse | undefined,
      );
      if (saved && typeof saved.Content === 'string')
        setOverrideSettings({
          ...(effectiveInviteSettings || {}),
          Content: saved.Content,
        });
      setSelectedSavedId(value);
    } catch {
      console.error('Failed to update selected multiple invite');
    }
    setIsSwitching(false);
  };

  useEffect(() => {
    if (!open) return;
    setPreviewHtml(processedHtml);
  }, [open, processedHtml]);

  const onSaveCustomized = async () => {
    const source = (activeSettings ||
      effectiveInviteSettings ||
      {}) as Partial<UpdateInviteSettingRequest> &
      Partial<InvestorInviteSetting>;
    const contentToSave =
      editHtml && editHtml.trim() ? editHtml : DEFAULT_CONTENT;
    const body = {
      IsActive: true,
      AdvisorGroupEmailIsActive: false,
      MultipleInviteId: null,
      InviteName: null,
      Content: contentToSave,
      UseGroupName:
        typeof useGroupName === 'boolean'
          ? useGroupName
          : typeof source.UseGroupName === 'boolean'
            ? source.UseGroupName
            : true,
      UseLastName:
        typeof useLastName === 'boolean'
          ? useLastName
          : typeof source.UseLastName === 'boolean'
            ? source.UseLastName
            : false,
      CustomSignature:
        customSignature && customSignature.length > 0
          ? customSignature
          : source.CustomSignature || t('bulkInvite.defaultSignature', 'Best,'),
      AdvisorId: source.AdvisorId ?? null,
      AdvisorGroupId: source.AdvisorGroupId ?? null,
      AdvisorName: source.AdvisorName,
      AdvisorGroupName: source.AdvisorGroupName,
      LoggedInUserName: source.LoggedInUserName ?? null,
      IncludeScheduleAssistance:
        typeof source.IncludeScheduleAssistance === 'boolean'
          ? source.IncludeScheduleAssistance
          : false,
    } as Partial<UpdateInviteSettingRequest>;

    try {
      await updateInviteSetting(body).unwrap();
      const refreshed = await refetchAdvisorSettings();
      if (refreshed?.data)
        setOverrideSettings(refreshed.data as InvestorInviteSetting);
    } catch {
      console.error('Failed to update invite setting');
    }
    setIsEditing(false);
  };

  // Initialize form fields when switching to custom or when settings change
  useEffect(() => {
    if (!open) return;
    if (invitationType !== 'custom') return;
    const src = (activeSettings ||
      effectiveInviteSettings ||
      {}) as Partial<UpdateInviteSettingRequest> &
      Partial<InvestorInviteSetting>;
    setUseLastName(
      typeof src.UseLastName === 'boolean' ? src.UseLastName : false,
    );
    setCustomSignature(
      String(src.CustomSignature || t('bulkInvite.defaultSignature', 'Best,')),
    );
    setUseGroupName(
      typeof src.UseGroupName === 'boolean' ? src.UseGroupName : true,
    );
  }, [open, invitationType, activeSettings, effectiveInviteSettings, t]);

  const isSmall = useMediaQuery('(max-width:900px)');
  const showInitialLoading =
    open &&
    (!initializedForOpen || initializing || isFetchingBase || isSwitching);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='lg'
      fullScreen={isFullscreen || isSmall}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography
          variant='h5'
          sx={{ fontSize: '1.25rem' }}
        >
          {t('bulkInvite.editInvitation', 'Edit Invitation')}
        </Typography>
        <IconButton
          aria-label={t('close', 'Close')}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid
          container
          spacing={3}
        >
          <Grid size={{ xs: 12, md: 4 }}>
            <InvitationTypeSelector
              value={invitationType}
              hasSavedOptions={hasSavedOptions}
              loading={showInitialLoading}
              onUseDefault={onUseDefault}
              onUseCustomized={onUseCustomized}
              onUseSaved={onUseSaved}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <Box
              sx={{
                border: (t) => `1px solid ${t.palette.divider}`,
                bgcolor: (t) => t.palette.grey[50],
                borderRadius: 1,
                maxHeight: 'none',
              }}
            >
              <Box sx={{ p: 2 }}>
                {showInitialLoading ? (
                  <Stack gap={1.5}>
                    <Skeleton
                      variant='text'
                      width={160}
                      height={28}
                    />
                    <Skeleton
                      variant='rounded'
                      height={180}
                    />
                    <Skeleton
                      variant='text'
                      width={120}
                      height={24}
                    />
                    <Skeleton
                      variant='rounded'
                      height={24}
                      width='75%'
                    />
                    <Skeleton
                      variant='rounded'
                      height={24}
                      width='55%'
                    />
                    <Skeleton
                      variant='rounded'
                      height={24}
                      width='65%'
                    />
                  </Stack>
                ) : (
                  invitationType === 'custom' &&
                  isEditing && (
                    <>
                      <InvitationCustomEditor
                        editHtml={editHtml}
                        setEditHtml={setEditHtml}
                        useLastName={useLastName}
                        setUseLastName={setUseLastName}
                        customSignature={customSignature}
                        setCustomSignature={setCustomSignature}
                        useGroupName={useGroupName}
                        setUseGroupName={setUseGroupName}
                        advisorName={String(activeSettings?.AdvisorName || '')}
                        advisorGroupName={String(
                          activeSettings?.AdvisorGroupName || '',
                        )}
                      />
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        gap={1}
                        justifyContent='flex-end'
                      >
                        <StyledButton
                          variant='outlined'
                          size='large'
                          onClick={() => setIsEditing(false)}
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                          {t('cancel', 'Cancel')}
                        </StyledButton>
                        <StyledButton
                          variant='contained'
                          size='large'
                          onClick={onSaveCustomized}
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                          {t('save', 'Save')}
                        </StyledButton>
                      </Stack>
                    </>
                  )
                )}
                {!showInitialLoading &&
                  (!isEditing || invitationType !== 'custom') && (
                    <Stack gap={1.5}>
                      {invitationType === 'custom' && (
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          justifyContent='flex-end'
                        >
                          <StyledButton
                            variant='contained'
                            size='large'
                            onClick={() => setIsEditing(true)}
                            sx={{ width: { xs: '100%', sm: 'auto' } }}
                          >
                            {t('edit', 'Edit')}
                          </StyledButton>
                        </Stack>
                      )}
                      <Typography
                        variant='h6'
                        sx={{ mb: 1 }}
                      >
                        {t(
                          'bulkInvite.previewHeader',
                          'What will my invitation look like?',
                        )}
                      </Typography>
                      {invitationType === 'saved' && (
                        <SavedInvitationSelector
                          options={savedOptions}
                          selectedId={selectedSavedId}
                          loading={showInitialLoading}
                          onChange={onChangeSavedOption}
                        />
                      )}
                      {!(invitationType === 'saved' && !selectedSavedId) && (
                        <>
                          <Typography
                            variant='subtitle1'
                            sx={{ mb: 0.5 }}
                          >
                            <b>{t('bulkInvite.message', 'Message')}</b>
                          </Typography>
                          <Box
                            id='invitation-content'
                            sx={{ typography: 'body2' }}
                            dangerouslySetInnerHTML={{ __html: previewHtml }}
                          />
                        </>
                      )}
                    </Stack>
                  )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default EditInvitationTemplateModal;
